import React, { useRef, useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { FaChevronLeft, FaChevronRight, FaPlay, FaPause, FaTimes } from 'react-icons/fa';
import Footer from '../Footer';
import './Galerija.css';

const SLIDESHOW_IMAGES = [
  { src: "./images/macka1.jpg", alt: "Veterinarski centar - prostor 1" },
  { src: "./images/image2.png", alt: "Veterinarski centar - prostor 2" },
  { src: "./images/image3.png", alt: "Veterinarski centar - prostor 3" }
];

const GALLERY_IMAGES = [
  "./images/animal12.jpg",
  "./images/animal5.jpg",
  "./images/animal1.jpg",
  "./images/animal17.jpg",
  "./images/animal16.jpg",
  "./images/animal14.jpg",
  "./images/animal2.jpg",
  "./images/animal10.jpg"
];

const AUTOPLAY_INTERVAL = 3000;
const SCROLL_AMOUNT = 420;

export default function Galerija() {
  const galleryRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % SLIDESHOW_IMAGES.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + SLIDESHOW_IMAGES.length) % SLIDESHOW_IMAGES.length);
  }, []);

  const toggleAutoPlay = useCallback(() => {
    setIsAutoPlay((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isAutoPlay) return;

    const interval = setInterval(nextSlide, AUTOPLAY_INTERVAL);
    return () => clearInterval(interval);
  }, [isAutoPlay, nextSlide]);

  const scrollGallery = useCallback((direction) => {
    if (!galleryRef.current) return;
    
    const scrollAmount = direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    galleryRef.current.scrollBy({
      left: scrollAmount,
      behavior: "smooth"
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const nextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === GALLERY_IMAGES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const prevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? GALLERY_IMAGES.length - 1 : prev - 1
    );
  }, []);

  const openModal = useCallback((index) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalOpen) return;
      
      if (e.key === 'ArrowRight') {
        nextImage(e);
      } else if (e.key === 'ArrowLeft') {
        prevImage(e);
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, nextImage, prevImage, closeModal]);

  return (
    <>
      <main className="galerija-container">
        <Helmet>
          <title>Galerija | Veterinarski centar</title>
          <meta 
            name="description" 
            content="Pogledajte galeriju našeg veterinarskog centra, moderne prostorije, opremu i uvjete za njegu vaših ljubimaca."
          />
          <meta name="keywords" content="veterinar, galerija, klinika, ljubimci, oprema, ambulanta" />
          <meta property="og:title" content="Galerija | Veterinarski centar" />
          <meta property="og:description" content="Pogledajte našu veterinarsku ordinaciju i opremu." />
          <meta property="og:type" content="website" />
        </Helmet>

        <section className="slideshow-section">
          <header className="slideshow-header">
            <h1 className="slideshow-title">Naš veterinarski centar</h1>
            <p className="slideshow-subtitle">
              Mjesto gdje vaši ljubimci dobivaju najbolju moguću skrb
            </p>
          </header>

          <div className="slideshow-wrapper">
            <button 
              className="slideshow-button prev-slide" 
              onClick={prevSlide}
              aria-label="Prethodna slika"
            >
              <FaChevronLeft />
            </button>

            <div className="slideshow-container">
              {SLIDESHOW_IMAGES.map((image, index) => (
                <div
                  key={`slide-${index}`}
                  className={`slide ${index === currentSlide ? 'active' : ''}`}
                >
                  <img 
                    src={image.src} 
                    alt={image.alt} 
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </div>
              ))}
            </div>

            <button 
              className="slideshow-button next-slide" 
              onClick={nextSlide}
              aria-label="Sljedeća slika"
            >
              <FaChevronRight />
            </button>

            <button 
              className="slideshow-toggle" 
              onClick={toggleAutoPlay}
              aria-label={isAutoPlay ? "Zaustavi automatsko prikazivanje" : "Pokreni automatsko prikazivanje"}
            >
              {isAutoPlay ? <FaPause /> : <FaPlay />}
            </button>
          </div>
        </section>

        <section className="manual-gallery-section">
          <header className="gallery-header">
            <h2 className="gallery-title">Galerija</h2>
          </header>

          <div className="gallery-wrapper">
            <button 
              className="gallery-button prev" 
              onClick={() => scrollGallery("left")}
              aria-label="Pomakni galeriju lijevo"
            >
              <FaChevronLeft />
            </button>

            <div ref={galleryRef} className="gallery-scroll" role="list">
              {GALLERY_IMAGES.map((img, index) => (
                <div 
                  key={index} 
                  className="gallery-item" 
                  role="listitem"
                  onClick={() => openModal(index)}
                >
                  <img 
                    src={img} 
                    alt={`Veterinarska galerija slika ${index + 1}`} 
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            <button 
              className="gallery-button next" 
              onClick={() => scrollGallery("right")}
              aria-label="Pomakni galeriju desno"
            >
              <FaChevronRight />
            </button>
          </div>
        </section>

        <div 
          className={`gallery-modal ${modalOpen ? 'active' : ''}`} 
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Galerija slika"
        >
          <button 
            className="gallery-modal-close" 
            onClick={closeModal}
            aria-label="Zatvori galeriju"
          >
            <FaTimes />
          </button>
          
          {GALLERY_IMAGES.length > 1 && (
            <>
              <button 
                className="gallery-modal-nav gallery-modal-prev" 
                onClick={prevImage}
                aria-label="Prethodna slika"
              >
                <FaChevronLeft />
              </button>
              <button 
                className="gallery-modal-nav gallery-modal-next" 
                onClick={nextImage}
                aria-label="Sljedeća slika"
              >
                <FaChevronRight />
              </button>
            </>
          )}
          
          <img 
            src={GALLERY_IMAGES[currentImageIndex]} 
            alt={`Galerija ${currentImageIndex + 1}`}
            className="gallery-modal-content"
            onClick={(e) => e.stopPropagation()}
          />
          
          <div className="gallery-modal-counter">
            {currentImageIndex + 1} / {GALLERY_IMAGES.length}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}